import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionScopesService } from '../services/scope.service';
import type { ConstructionScope, ScopeFilters } from '../types/construction-scope.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useConstructionScopes(companyId: string, initialFilters: ScopeFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ScopeFilters>({
    status: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['construction-scopes', companyId, filters],
    queryFn: () => ConstructionScopesService.getScopes(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['construction-scope-stats', companyId],
    queryFn: () => ConstructionScopesService.getScopeStats(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ConstructionScope>) => ConstructionScopesService.createScope(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-scopes'] });
      queryClient.invalidateQueries({ queryKey: ['construction-scope-stats'] });
      toast.success('Khởi tạo phạm vi thi công thành công');
    },
  });

  return {
    scopes: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    createScope: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

export function useConstructionScopeDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-scopes', 'detail', id],
    queryFn: () => ConstructionScopesService.getScopeDetail(id!),
    enabled: !!id,
  });

  const itemsQuery = useQuery({
    queryKey: ['construction-scopes', 'items', id],
    queryFn: () => ConstructionScopesService.getScopeItems(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<ConstructionScope>) => ConstructionScopesService.updateScope(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-scopes'] });
      queryClient.invalidateQueries({ queryKey: ['construction-scopes', 'detail', id] });
      toast.success('Cập nhật phạm vi thành công');
    },
  });

  return {
    scope: query.data,
    items: itemsQuery.data || [],
    isLoading: query.isLoading || itemsQuery.isLoading,
    updateScope: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
